'use client';

import { CSSProperties } from 'react';

import { DocTip, VideoWithPlaceholder } from '@kezios/design-system';
import { Button, Tabs } from '@mantine/core';
import { CaptionVideo } from 'components/website/components/videos/CaptionVideo/CaptionVideo';
import { PodcastVideo } from 'components/website/components/videos/PodcastVideo/PodcastVideo';
import { RepeatVideo } from 'components/website/components/videos/RepeatVideo/RepeatVideo';
import { ResizeVideo } from 'components/website/components/videos/ResizeVideo/ResizeVideo';
import { SilenceVideo } from 'components/website/components/videos/SilenceVideo/SilenceVideo';
import { ZoomVideo } from 'components/website/components/videos/ZoomVideo/ZoomVideo';
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';

export const MDXRemoteProxy = ({
  source,
  lazy = false,
}: {
  source: MDXRemoteSerializeResult;
  lazy?: boolean;
}) => (
  <MDXRemote
    {...source}
    lazy={lazy}
    components={{
      VideoWithPlaceholder,
      TextColor: props => (
        <span style={{ color: props.color ?? '#EB5B26' }}>
          {props.children}
        </span>
      ),
      Tip: DocTip,
      Button: props => (
        <Button
          {...(props as any)}
          ref={null}
          size="md"
          type="button"
          component="a"
          style={{
            margin: '8px 0',
            '&:first-child': {
              marginTop: 'unset',
            },
          }}
        >
          {typeof props.children === 'object' &&
          'props' in props.children &&
          props.children.type === 'p'
            ? props.children.props.children
            : props.children}
        </Button>
      ),
      a: props => (
        <a
          {...props}
          style={
            {
              color: 'var(--mantine-color-primary-7)',
              textDecoration: 'underline',
              marginTop: 0,

              '&:hover': {
                color: 'var(--mantine-color-primary-9)',
              },
            } as CSSProperties
          }
        />
      ),
      h1: props => <h1 id="title" {...props} />,
      h2: props => <h2 id="title" {...props} />,
      h3: props => <h3 id="title" {...props} />,
      h4: props => <h4 id="title" {...props} />,
      h5: props => <h5 id="title" {...props} />,
      h6: props => <h6 id="title" {...props} />,
      YoutubeVideo: props => (
        <iframe
          width="560"
          height="315"
          src={props.src}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={props.title}
        />
      ),
      Arcade : props => (
        <div style={{ position: 'relative', paddingBottom: 'calc(62.5% + 41px)', height: 0, width: '100%' }}>
          <iframe
            src={`https://demo.arcade.software/${props.id}?embed&embed_mobile=inline&embed_desktop=inline&show_copy_link=true"`}
            title={props.title}
            frameBorder="0"
            loading="lazy"
            allowFullScreen
            allow="clipboard-write"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', colorScheme: 'light' }}
          />
        </div>
      ),
      Tabs: props => <Tabs {...props} />,
      TabsList: props => <Tabs.List {...props} />,
      TabsTab: props => <Tabs.Tab {...props} />,
      TabsPanel: props => <Tabs.Panel {...props} />,
      PodcastHeroVideo: props => (
        <PodcastVideo scrollPosition={0} loop {...props} />
      ),
      AutoCaptionsHeroVideo: props => (
        <CaptionVideo scrollPosition={0} loop {...props} />
      ),
      AutoCutSilencesHeroVideo: props => (
        <SilenceVideo scrollPosition={0} loop {...props} />
      ),
      AutoZoomVideo: props => <ZoomVideo scrollPosition={0} loop {...props} />,
      AutoResize: props => <ResizeVideo scrollPosition={0} loop {...props} />,
      AutoRepeat: props => <RepeatVideo scrollPosition={0} loop {...props} />,
      Center: props => (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {props.children}
        </div>
      ),
    }}
  />
);
